import icBack from "@/assets/icons/ic-arrow-narrow-left.svg";
import icCalendar from "@/assets/icons/ic-calendar-03.svg";
import icPen from "@/assets/icons/ic-pen.svg";
import icPerson from "@/assets/icons/ic-person.svg";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/lib/permissions";
import { formatDate } from "@/lib/utils";
import { AdminAxNewsService, IArticle, IStatus } from "@/services/ax-news-service";
import DOMPurify from "dompurify";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SafeImage } from "@/components/ui/safe-image/safe-image";
const CATEGORY_BADGE_LABELS: Record<string, string> = {
  ai_use_case: "AI USE CASE",
  ax_trend: "AX TREND",
  pwc_digital_solutions: "PWC DIGITAL SOLUTIONS",
};

const Status_BADGE_LABELS: Record<IStatus, string> = {
  published: "게시됨",
  scheduled: "예약됨",
};

export const AxNewsDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canUpdate = can.update("ax_trend_hub");

  const [article, setArticle] = useState<IArticle | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setIsLoading(true);

    AdminAxNewsService.getById(id)
      .then((res) => {
        if (!cancelled && res.data?.success && res.data?.data) {
          setArticle(res.data.data);
        }
      })
      .catch(() => {
        if (!cancelled) setArticle(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!article) {
    return null;
  }

  const badgeLabel = CATEGORY_BADGE_LABELS[article.category] ?? article.category;

  const statusBadgeLabel = Status_BADGE_LABELS[article.status] ?? article.status;
  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div
        className="flex flex-col gap-6 w-[896px] mx-auto  lg:px-0"
        style={{ display: isLoading ? "none" : "flex" }}
      >
        {/* Back button */}
        <div className="flex justify-between items-center gap-4">
          <button
            type="button"
            onClick={() => navigate("/ax-trend-hub")}
            aria-label="뒤로 가기"
            className="w-12 h-12 flex items-center justify-center "
          >
            <img src={icBack} />
          </button>
          {canUpdate && (
            <Button
              onClick={() => navigate(`/ax-trend-hub/${article.id}/edit`)}
              className="h-[48px] bg-primary font-bold"
            >
              <img src={icPen} alt="" /> 게시글 수정
            </Button>
          )}
        </div>

        <div className="bg-white  rounded-t">
          {/* Header: badges + title + image */}
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              {/* Badges row: section + date */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-[4px] items-center justify-center h-8 px-3 bg-[#EBEBEB] rounded text-caption font-medium text-content-strong uppercase tracking-wide whitespace-nowrap">
                  {badgeLabel}
                </div>
                <div className="uppercase flex gap-[4px] items-center  h-8 px-3 bg-[#EBEBEB] rounded text-caption font-medium text-content-strong whitespace-nowrap">
                  <img src={icPerson} alt="" />
                  등록자: {article.created_by_name}
                </div>

                <div className="flex gap-[4px] items-center  h-8 px-3 bg-[#EBEBEB] rounded text-caption font-medium text-content-strong whitespace-nowrap">
                  <img src={icCalendar} alt="" width={16} height={16} />
                  {formatDate(article.publish_at || article.created_at, "YYYY-MM-DD HH:mm")}
                </div>
              </div>

              {/* Title */}
              <div className="flex justify-between items-center">
                <h1 className="text-[32px] leading-[40px] tracking-[-0.96px] text-content-strong font-medium">
                  {article.title}
                </h1>
              </div>
            </div>

            {/* Hero image */}
            <div className="relative w-full h-[320px] rounded overflow-hidden bg-bg-fill-neutral-subtle">
              <SafeImage
                src={article.cover_image_url}
                alt={article.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Article body */}
          <article
            className="prose prose-neutral mx-auto w-full flex flex-col gap-6 leading-6 tracking-[-0.16px]  whitespace-pre-line pt-4"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(article.content),
            }}
          />
        </div>
        <div className="flex justify-between border-t border-[#00000017] py-3 mt-[-24px]">
          <div className="flex flex-col ">
            <span className="text-[#8D8D8D] text-[16px]">등록일</span>
            <span className="text-body text-[#202020]">{formatDate(article.created_at)}</span>
          </div>
          {article.updated_at && article.updated_at !== article.created_at && (
            <div className="flex flex-col items-end">
              <span className="text-[#B0B0B0] text-[16px]">최종 수정일</span>
              <span className="text-body text-[#000000]">
                {formatDate(article.updated_at, "YYYY-MM-DD HH:mm")}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AxNewsDetailPage;
